// Bulk-hook voor de meetmoment-signalering: haalt de gegevens van één
// organisatie in enkele .get()-aanroepen op en laat de pure helpers het
// rekenwerk doen. Geen onSnapshot — verversen is de component opnieuw mounten.
//
// Dit is een directe read buiten de useFirestore-engine om, dus de tenantgrens
// en het soft-delete-filter staan hier met de hand. Beide zijn verplicht.
//
// AI-CONTEXT: See docs/patterns.md#retention-derivation
// AI-CONTEXT: See docs/pitfalls.md#database-missing-compagnyid-on-fresh-fetch

import { useEffect, useState } from 'react'
import { db } from '../../firebase/config'
import { client } from '../Client'
import notDeleted from '../../helpers/notDeleted'
import buildMeasurementSignals from '../../helpers/measurementSignals/buildMeasurementSignals'
import buildAnonymousResearchRows from '../../helpers/measurementSignals/buildAnonymousResearchRows'
import effectiveResearchIds from '../../helpers/measurementSignals/effectiveResearchIds'
import deriveResearchAggregate from '../../helpers/measurementSignals/deriveResearchAggregate'
import { getResearchAggregate } from './researchAggregateCache'

/** Een leeg antwoord van een geweigerde read is niet te onderscheiden van 'geen
 *  gegevens'. Daarom onthouden we wélke collectie het liet afweten, zodat het
 *  scherm kan zeggen dat het beeld onvolledig is in plaats van een lege lijst
 *  als waarheid te presenteren. */
const fetchTenantDocs = async (collection, unavailable, scope) => {
    try {
        let query = db.collection(collection).where('CompagnyID', '==', client)
        // Beide filters zijn gelijkheidsfilters; Firestore combineert daarvoor
        // enkelvoudige indexen, dus er is geen samengestelde index nodig.
        if (scope && scope.value) query = query.where(scope.field, '==', scope.value)
        const snapshot = await query.get()
        return snapshot.docs.map((entry) => ({ ...entry.data(), docid: entry.id })).filter(notDeleted)
    } catch (error) {
        // eslint-disable-next-line no-console
        console.error(`useMeasurementSignalsBulk: ${collection} ophalen mislukt`, error)
        unavailable.add(collection)
        return null
    }
}

const fetchResponsesForResearch = async (researchId, unavailable) => {
    try {
        const snapshot = await db.collection('QuestionnairesResponses')
            .where('CompagnyID', '==', client)
            .where('ResearchID', '==', researchId)
            .get()
        return snapshot.docs.map((entry) => entry.data()).filter(notDeleted)
    } catch (error) {
        // eslint-disable-next-line no-console
        console.error('useMeasurementSignalsBulk: antwoorden ophalen mislukt', error)
        unavailable.add('QuestionnairesResponses')
        // `null`, niet `[]`. Een geweigerde read die als lege lijst terugkomt is
        // niet te onderscheiden van een onderzoek zonder antwoorden — en dat
        // laatste betekent "niemand heeft ingevuld", een stellige bewering.
        return null
    }
}

/**
 * Eén deelnemer of huishouden in de vorm die de rekenhelpers verwachten.
 *
 * `researchIds` is de EFFECTIEVE set: wat met de hand gekoppeld is plus elk
 * onderzoek dat via de schakelaar voor deze hele soort geldt. Bewust een andere
 * naam dan het Firestore-veld `Research`: een rauw document dat hier per
 * ongeluk langs de helper heen komt, levert dan nul rijen op in plaats van stil
 * alleen de handmatige set.
 * AI-CONTEXT: See docs/pitfalls.md#dev-second-source-never-reaches-its-own-gate
 *
 * @param {object} doc - Het Personas- of Adresses-document
 * @param {'persona'|'adress'} kind
 * @param {Array<object>|null} researchDocs - Alle Research-documenten van de organisatie
 */
const toEntity = (doc, kind, researchDocs) => ({
    ID: doc.ID,
    kind,
    name: (kind === 'persona' ? doc.Name : doc.Title) || '',
    status: doc.Status || '',
    StartDate: doc.StartDate,
    Timestamp: doc.Timestamp,
    researchIds: effectiveResearchIds({ kind, linkedIds: doc.Research, researches: researchDocs }),
})

/**
 * @typedef {object} MeasurementSignalsState
 * @property {Array<import('../../helpers/measurementSignals/buildMeasurementSignals').MeasurementSignalRow>} rows
 * @property {Array<import('../../helpers/measurementSignals/buildAnonymousResearchRows').AnonymousResearchRow>} anonymousRows
 *   Onderzoeken zonder herleidbare invullers — groepsmetingen. Alleen gevuld met
 *   `includeAnonymous`, en nooit op een detailpagina: daar gaat het niet over de hele
 *   organisatie.
 * @property {boolean} loaded - `false` betekent 'nog niet bekend', niet 'niets gevonden'
 * @property {Array<string>} unavailable - Collecties die niet gelezen konden worden
 */

/**
 * De meetmoment-signalen van de huidige organisatie.
 *
 * `loaded` is een eigen waarde en geen afgeleide van `rows.length`: zolang de
 * gegevens onderweg zijn mag geen enkel scherm beweren dat er niets openstaat.
 *
 * AI-CONTEXT: See docs/pitfalls.md#ui-empty-state-claims-absence-while-loading
 *
 * @param {object} [options]
 * @param {string} [options.entityId] - Beperk tot één deelnemer of huishouden. Op een
 *   detailpagina scheelt dat de antwoorden van elk ánder onderzoek van de organisatie;
 *   de afgeleide tussentijd blijft kloppen, want binnen het eigen onderzoek wordt nog
 *   steeds alles gelezen.
 * @param {'persona'|'adress'} [options.entityKind] - Bij een `entityId`: welke soort. Scheelt
 *   het ophalen van de hele andere collectie.
 * @param {boolean} [options.enabled] - `false` haalt niets op. Zet dit in plaats van de
 *   provider voorwaardelijk te mounten: een provider die tijdens het laden verschijnt,
 *   wisselt het elementtype op die plek en laat React de hele boom eronder opnieuw
 *   opbouwen — met alle Firestore-reads van elke kaart erin.
 * @param {boolean} [options.includeAnonymous] - Ook de onderzoeken lezen die géén enkele
 *   deelnemer of huishouden bereiken. Dat zijn de groepsmetingen, en die zijn nergens anders
 *   te zien — maar het is wél een lees-ronde extra per zo'n onderzoek. Zet dit alleen aan als
 *   de kaart 'Anonieme onderzoeken' er ook echt staat; standaard uit, want verreweg de meeste
 *   dashboards doen er niets mee (docs/inbox.md #246).
 * @returns {MeasurementSignalsState}
 */
const useMeasurementSignalsBulk = ({ entityId, entityKind, enabled = true, includeAnonymous = false } = {}) => {
    const [state, setState] = useState({ rows: [], anonymousRows: [], loaded: false, unavailable: [] })

    useEffect(() => {
        let cancelled = false

        const run = async () => {
            if (!enabled) return

            // Wisselt de deelnemer, dan is de vorige uitkomst niet meer waar.
            // Zonder deze reset blijft `loaded` true en filtert de volgende
            // deelnemer zich tot een lege lijst — wat op het scherm leest als
            // 'geen meetmomenten' terwijl het 'nog niet bekend' is.
            // AI-CONTEXT: See docs/pitfalls.md#ui-empty-state-claims-absence-while-loading
            setState((previous) => (previous.loaded
                ? { rows: [], anonymousRows: [], loaded: false, unavailable: [] }
                : previous))

            const unavailable = new Set()
            const ownScope = entityId ? { field: 'ID', value: entityId } : null

            const [personaDocs, adressDocs, researchDocs, momentDocs, statusDocs] = await Promise.all([
                entityKind === 'adress' ? [] : fetchTenantDocs('Personas', unavailable, ownScope),
                entityKind === 'persona' ? [] : fetchTenantDocs('Adresses', unavailable, ownScope),
                fetchTenantDocs('Research', unavailable),
                fetchTenantDocs('MeasureMoments', unavailable),
                fetchTenantDocs('StatusHistory', unavailable,
                    entityId ? { field: 'EntityID', value: entityId } : null),
            ])

            if (cancelled) return

            const researchById = {}
            ;(researchDocs || []).forEach((doc) => { if (doc.ID) researchById[doc.ID] = doc })

            // Wie doet mee: handmatig gekoppeld óf via de schakelaar op het
            // onderzoek. Een geweigerde Research-read geeft `null`; dan blijft
            // alleen de handmatige set over en meldt `unavailable` het gat.
            const entities = [
                ...(personaDocs || []).map((doc) => toEntity(doc, 'persona', researchDocs)),
                ...(adressDocs || []).map((doc) => toEntity(doc, 'adress', researchDocs)),
            ].filter((entity) => entity.ID
                && entity.researchIds.length > 0
                && (!entityId || entity.ID === entityId))

            const momentsByResearch = {}
            ;(momentDocs || []).forEach((doc) => {
                if (!doc.ResearchID) return
                ;(momentsByResearch[doc.ResearchID] = momentsByResearch[doc.ResearchID] || []).push(doc)
            })

            // Onderzoeken die minstens één deelnemer of huishouden bereiken —
            // met de hand of via de schakelaar — én meetmomenten hebben.
            const linkedResearchIds = [...new Set(
                entities.flatMap((entity) => entity.researchIds)
            )].filter((id) => researchById[id] && momentsByResearch[id])

            // Standaard blijft het daarbij — dat houdt de ophaalactie klein.
            // Alleen als de kaart 'Anonieme onderzoeken' er staat lezen we óók
            // de onderzoeken die niemand bereiken: dat zijn juist de
            // groepsmetingen, en die zijn nergens anders te zien omdat er geen
            // deelnemer aan hangt. Op een detailpagina nooit: daar gaat het
            // over deze persoon.
            const leesAlleOnderzoeken = includeAnonymous && !entityId
            const researchIdsToRead = leesAlleOnderzoeken
                ? Object.keys(momentsByResearch).filter((id) => researchById[id])
                : linkedResearchIds

            // Per onderzoek is de afgeleide set voor élke deelnemer dezelfde, dus
            // hij gaat door een gedeelde cache. Zonder die cache leest elke
            // detailpagina alle antwoorden van elk onderzoek dat dit huishouden
            // bereikt opnieuw — sinds 'Geldt voor alle huishoudens' is dat élk
            // onderzoek. Zie docs/inbox.md #246.
            const perResearch = await Promise.all(
                researchIdsToRead.map((id) => getResearchAggregate(id, async () => {
                    const responses = await fetchResponsesForResearch(id, unavailable)
                    // `null` blijft `null`: een geweigerde read mag niet als
                    // 'geen antwoorden' de cache in.
                    if (responses === null) return null
                    return deriveResearchAggregate({
                        researchId: id,
                        responses,
                        moments: momentsByResearch[id] || [],
                    })
                }).then((aggregate) => ({ id, aggregate })))
            )

            const fills = []
            // Per meetmoment: staan er naamloze antwoorden op? Alleen dán kan
            // het antwoord van iemand daartussen schuilgaan en is afwezigheid
            // geen zekerheid. Elk meetmoment van een gelezen onderzoek krijgt
            // een waarde, ook als er nul antwoorden zijn — dat is juist het
            // geval waarin afwezigheid het meest zeker is.
            const anonymousByMoment = {}
            const intervalByResearch = {}
            const aggregateByResearch = {}

            perResearch.forEach(({ id, aggregate }) => {
                // Een mislukte read levert `null`. Dan blijven de meetmomenten
                // van dit onderzoek buiten `anonymousByMoment`, zodat ze op
                // 'niet te zeggen' uitkomen in plaats van op een stellige
                // 'nog niet ingevuld'.
                aggregateByResearch[id] = aggregate || null
                if (!aggregate) {
                    intervalByResearch[id] = null
                    return
                }

                fills.push(...aggregate.fills)
                Object.assign(anonymousByMoment, aggregate.anonymousByMoment)
                intervalByResearch[id] = aggregate.interval
            })

            if (cancelled) return

            const now = Date.now()

            setState({
                rows: buildMeasurementSignals({
                    entities,
                    researchById,
                    momentsByResearch,
                    fills,
                    anonymousByMoment,
                    intervalByResearch,
                    statusEntries: statusDocs || [],
                    now,
                }),
                anonymousRows: leesAlleOnderzoeken ? buildAnonymousResearchRows({
                    researchById,
                    momentsByResearch,
                    aggregateByResearch,
                    linkedResearchIds,
                    now,
                }) : [],
                loaded: true,
                unavailable: [...unavailable].sort(),
            })
        }

        run()
        return () => { cancelled = true }
    }, [entityId, entityKind, enabled, includeAnonymous])

    return state
}

export default useMeasurementSignalsBulk
